<template>
  <!-- A caixa só existe para o transbordo: sem ele ela é `display: contents`, e as abas ficam no
       layout de quem hospeda como se ela não estivesse ali. -->
  <div ref="wrap" class="ontos-view-tabs-box" :class="{'ontos-view-tabs-box--overflow': overflow}">
    <!-- A escolha sai do CLIQUE na aba, e não do `update:model-value` do grupo: o grupo também emite
         por conta própria — quando a aba escolhida no "Mais" entra na linha, ela ainda não está
         registrada no instante em que o valor chega, e o grupo emitia uma seleção que ninguém fez
         (vazia, ou a primeira, pelo `mandatory: 'force'`). O design lia isso como "sem aba" e voltava
         para Coleções (relato do usuário). Pelo clique, só o que a pessoa escolheu sobe.

         Mas o ouvinte vazio do `update:model-value` NÃO pode sair: é ele que faz o `v-tabs` ser
         controlado. O `useProxiedModel` do Vuetify só segue o `model-value` de fora quando há também
         um ouvinte; sem ele, o grupo guarda estado próprio. Foi o que aconteceu quando o ouvinte saiu:
         a aba escolhida no "Mais" entrava na linha, a que ela substituía saía antes de ela se
         registrar, o grupo achava nada selecionado e, pelo `mandatory: 'force'`, acendia a primeira —
         e o conteúdo era de Acesso com Informações acesa (relato do usuário). Controlado, ele sempre
         mostra o que recebe, e a aba nova acende assim que se registra. -->
    <v-tabs :model-value="modelValue" color="primary" density="compact" class="ontos-view-tabs"
            :stacked="stacked" @update:model-value="() => undefined">
      <v-tab v-for="view of shown" :key="view.value" :value="view.value" :to="view.to"
             class="shell-plain-case" :title="compact ? view.title : undefined" @click="pick(view)">
        <v-icon v-if="view.icon" :start="!compact && !stacked" size="small">{{ view.icon }}</v-icon>
        <template v-if="!compact">{{ view.title }}</template>
        <v-badge v-if="view.alert" color="error" dot inline/>
      </v-tab>
    </v-tabs>
    <v-menu v-if="folded.length" location="bottom end">
      <template #activator="{ props }">
        <v-btn v-bind="props" variant="text" size="small" class="shell-plain-case"
               append-icon="mdi-chevron-down">{{ $tt('more') }}</v-btn>
      </template>
      <v-list density="compact">
        <!-- Com o ícone da aba, para o item do menu e a aba se reconhecerem (pedido do usuário) -->
        <v-list-item v-for="view of folded" :key="view.value" :title="view.title" :to="view.to"
                     :prepend-icon="view.icon" @click="pick(view)"/>
      </v-list>
    </v-menu>
    <!-- A régua: TODAS as abas e o "Mais", invisíveis e fora do fluxo, para saber quanto cada uma
         mede mesmo quando está dentro do menu. Medindo só as que aparecem, a que foi para o menu
         deixava de ser medida e não voltava mais quando a largura crescesse. -->
    <div v-if="overflow" class="ontos-view-tabs-measure" aria-hidden="true" inert>
      <v-tabs density="compact" class="ontos-view-tabs" :stacked="stacked">
        <v-tab v-for="(view, index) of views" :key="view.value" class="shell-plain-case"
               :ref="(tab: any) => fit.measure(index, tab?.$el)">
          <v-icon v-if="view.icon" :start="!compact && !stacked" size="small">{{ view.icon }}</v-icon>
          <template v-if="!compact">{{ view.title }}</template>
        </v-tab>
      </v-tabs>
      <v-btn :ref="(button: any) => measureMore(button?.$el)" variant="text" size="small"
             class="shell-plain-case" append-icon="mdi-chevron-down">{{ $tt('more') }}</v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Setup, toNative} from 'vue-facing-decorator'
import {Ref, ref, useTemplateRef} from 'vue'
import {RouteLocationRaw} from 'vue-router'
//Da raiz, e não de '@vuetify/v0/composables': o `moduleResolution: node` do projeto não lê os
//subcaminhos do `exports`, e a raiz traz o mesmo, sem o resto entrar no pacote (tree-shaking)
import {createOverflow, OverflowContext} from '@vuetify/v0'

/** Uma visão alternável: o valor, o ícone, o rótulo e, quando é navegação, o destino. */
export type ViewTab = {
  value: string
  /** Sem ícone, a aba é só o texto, também no telefone — ver o `compact`. */
  icon?: string
  title: string
  /** Só quando a troca de visão é navegação; sem ele, a aba emite a escolha. */
  to?: RouteLocationRaw
  /** O ponto vermelho: há o que corrigir nela — a aba da janela de propriedade que não confere. */
  alert?: boolean
}

type Fit = OverflowContext & { moreWidth: Ref<number> }

/**
 * A faixa de abas que alterna o que uma parte da tela mostra — Lista e Kanban, ou as partes do
 * design do ambiente.
 *
 * Serve a lugares que fazem a mesma coisa por baixo de formas diferentes: a coleção, onde
 * trocar de visão é NAVEGAR (cada aba tem `to`), e o bloco de um relacionamento na ficha e o design
 * do ambiente, onde é só trocar o que se desenha (sem `to`, a aba emite). Vuetify aceita os dois no
 * mesmo componente, e é por isso que a aparência cabe numa peça só.
 *
 * **Por que estas abas não parecem as da barra.** As de cima navegam entre coleções irmãs;
 * estas alternam a visão do mesmo conteúdo. O que as separa não é a forma — as duas são abas
 * com deslizador, e é isso que as faz LEREM como aba — mas o lugar, a cor e a caixa: lá branco
 * sobre a cor do modo, no alto da janela e em MAIÚSCULAS, que é como se lê navegação; aqui
 * primary sobre o fundo claro, na linha do título, em caixa mista.
 *
 * Já estiveram misturadas com os botões, sem deslizador e com a ativa em `tonal`: assim uma aba
 * deixa de parecer aba. O que a faz parecer é o deslizador.
 */
@Component({emits: ['update:modelValue'], name: 'ontos-view-tabs'})
class ViewTabs extends Vue {
  @Prop({type: Array as () => ViewTab[], required: true}) readonly views!: ViewTab[]
  @Prop({type: String, default: ''}) readonly modelValue!: string
  /**
   * As abas que não couberem vão para um menu "Mais ▾" no fim da linha, e não para setas de
   * rolagem — as setas, com cinco abas de texto no telefone, ficaram ruins (relato do usuário). Para
   * quem ocupa a largura que tiver: o design do ambiente, com as abas no lugar do título.
   */
  @Prop({type: Boolean, default: false}) readonly overflow!: boolean

  /**
   * Quantas abas cabem: o `createOverflow` do Vuetify0, que acompanha a largura da caixa e soma a
   * das abas medidas na régua. O que se reserva é o "Mais", que só existe quando algo transborda.
   */
  @Setup(() => {
    const moreWidth = ref(0)
    return {moreWidth, ...createOverflow({container: useTemplateRef('wrap'), reserved: () => moreWidth.value})}
  })
  readonly fit!: Fit

  /**
   * A aba escolhida, da linha ou do menu. A que navega (`to`) já foi pelo link; a de agora não
   * muda nada, e não sobe.
   */
  pick(view: ViewTab) {
    if (!view.to && view.value !== this.modelValue) this.$emit('update:modelValue', view.value)
  }

  measureMore(el: HTMLElement | undefined) {
    const width = el ? el.offsetWidth : 0
    if (width && width !== this.fit.moreWidth.value) this.fit.moreWidth.value = width
  }

  /**
   * As abas da linha. Cabendo todas — ou antes de haver o que medir —, todas, sem o "Mais". Senão,
   * as que couberem ao lado dele, e a ABERTA sempre entre elas, no lugar da última: aba escolhida
   * escondida no menu não mostraria onde se está.
   */
  get shown(): ViewTab[] {
    const width = this.fit.width.value
    if (!this.overflow || !width || this.fit.total.value <= width) return this.views
    const fits = Math.max(1, this.fit.capacity.value)
    const shown = this.views.slice(0, fits)
    const open = this.views.findIndex(view => view.value === this.modelValue)
    if (open >= fits) shown.splice(fits - 1, 1, this.views[open])
    return shown
  }

  /** As do menu, na ordem delas. */
  get folded(): ViewTab[] {
    return this.views.filter(view => !this.shown.includes(view))
  }

  /**
   * Na largura de telefone, só o ícone, com o rótulo virando tooltip nativo — o mesmo que o
   * ontos-header-action faz com os botões que ficam ao lado. É ali que as abas disputam a linha
   * com o título e as ações. Abas sem ícone ficam com o texto: sem ele não sobraria nada.
   */
  get compact(): boolean {
    return this.$vuetify.display.xs && !this.overflow && this.views.every(view => view.icon)
  }

  /**
   * As abas com transbordo — as dos designs —, no telefone, EMPILHADAS: o ícone em cima e o texto
   * embaixo, o `stacked` do Vuetify (pedido do usuário). Cada aba fica estreita, e cabem mais antes do
   * "Mais"; só com ícone, como as de Lista/Kanban, cinco ou seis abas viravam uma fileira de símbolos
   * a decifrar.
   */
  get stacked(): boolean {
    return this.$vuetify.display.xs && this.overflow && this.views.every(view => view.icon)
  }
}

export default toNative(ViewTabs)
</script>

<style scoped>
.ontos-view-tabs-box {
  display: contents;
}

/* A largura inteira de quem hospeda, e não a do conteúdo: é ela que o `createOverflow` mede, e
   medida pelo conteúdo ela nunca seria menor que as abas. */
.ontos-view-tabs-box--overflow {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  min-width: 0;
}

.ontos-view-tabs-box--overflow > .ontos-view-tabs {
  flex: 0 1 auto;
  min-width: 0;
}

/* A régua não aparece, não ocupa lugar e não recebe clique, mas mede cada aba no tamanho natural
   dela: `max-content`, e não a largura da caixa, que apertaria as abas. */
.ontos-view-tabs-measure {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  width: max-content;
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}

/* O min-width de 90px por aba é do Vuetify e serve à barra, onde as abas ocupam a
   largura toda. Aqui são duas, à esquerda de uma linha larga: medem o próprio conteúdo
   para não virarem dois blocos desproporcionais. */
.ontos-view-tabs :deep(.v-tab) {
  min-width: 0;
  padding: 0 16px;
}

/* Dentro de um cabeçalho as abas dividem a linha com os botões e precisam da altura deles —
   quem faz isso é o próprio cabeçalho, no `.page-header-tabs`, que é quem sabe o tamanho dos
   vizinhos. Aqui fica só o aperto do xs, que é sobre largura de telefone e não sobre alinhar. */
@media (max-width: 599px) {
  .ontos-view-tabs :deep(.v-tab) {
    padding: 0 8px;
  }
}
</style>
